import {
  type ITableSettings,
  isTableCellHeader,
} from "@library-elements/table";
import { stripStyleDecls } from "./strip-style-decls";

let lastTableId: null | string = null;
let lastSettings: Record<string, unknown> = {};
let touchedCells: Record<string, unknown> = {};

export const initTableOverwritesHandler = ({ settings, tableId }: IProps) => {
  if (tableId === lastTableId) return;
  lastTableId = tableId;
  lastSettings = { ...settings };
  touchedCells = {};
};

export const clearTableOverwritesHandler = (tableId: string) => {
  if (tableId !== lastTableId) return;
  lastTableId = null;
  lastSettings = {};
  touchedCells = {};
};

// Refresh the change-detection snapshot WITHOUT resetting any per-cell
// overrides. A skipOverwrites writer (the borders popover) intentionally
// changes table-level defaults alongside per-cell values; if the snapshot
// isn't refreshed, the next non-skip edit diffs against the stale values and
// wipes the per-cell border work.
export const syncTableOverwritesSnapshot = ({
  settings,
  tableId,
}: Omit<IProps, "forceOverwritesOn">) => {
  if (tableId !== lastTableId) return;
  lastSettings = { ...lastSettings, ...settings };
};

const shouldResetSetting = (
  key: string,
  { settings, forceOverwritesOn }: IProps,
): boolean => {
  if (forceOverwritesOn?.includes(key)) {
    return true;
  }

  let val = false;
  if (lastSettings[key] != null) {
    val = lastSettings[key] !== settings[key as keyof typeof settings];
  }
  (lastSettings[key] as unknown) = settings[key as keyof typeof settings];
  return val;
};

interface IProps {
  tableId: string;
  settings: ITableSettings;
  forceOverwritesOn?: string[];
}

export const handleTableOverwrites = (props: IProps) => {
  const { tableId } = props;
  if (tableId !== lastTableId) return;

  handleBaseSettings(props);
  handleFroalaSettings(props);
};

const handleBaseSettings = (props: IProps) => {
  const { settings } = props;
  const shouldReset = (key: string) => shouldResetSetting(key, props);

  const toReset = {
    bgColor: shouldReset("tableCellBgColor"),
    borderColor: shouldReset("tableCellBorderColor"),
    borderStyle: shouldReset("tableCellBorderStyle"),
    borderWidth: shouldReset("tableCellBorderWidth"),
    verticalAlign: shouldReset("tableCellVerticalAlignment"),
    //
    altBgColor: shouldReset("tableAlternatingRowsBgColor"),
    altBorderColor: shouldReset("tableAlternatingRowsBorderColor"),
    //
    headerBgColor: shouldReset("tableHeaderBgColor"),
    headerBorderColor: shouldReset("tableHeaderBorderColor"),
  };

  if (!Object.values(toReset).includes(true)) {
    return;
  }

  const {
    tableData,
    tableCols,
    tableRows,

    tableHasHeader,
    tableHeaderPosition,
    tableHasAlternatingRows,
  } = settings;

  for (let cellIdx = 0; cellIdx < tableData.length; cellIdx++) {
    const cell = tableData[cellIdx];
    const row = Math.floor(cellIdx / tableCols);
    const isHeader = isTableCellHeader({
      cellIdx,
      tableCols,
      tableRows,
      tableHasHeader,
      tableHeaderPosition,
    });
    const isAlt = !isHeader && tableHasAlternatingRows && row % 2 !== 0;

    const resetBorderProp = (prop: "Color" | "Style" | "Width") => {
      cell[`border${prop}`] = undefined;
      cell[`borderLeft${prop}`] = undefined;
      cell[`borderRight${prop}`] = undefined;
      cell[`borderTop${prop}`] = undefined;
      cell[`borderBottom${prop}`] = undefined;
    };

    if (
      (toReset.bgColor && !isAlt && !isHeader) ||
      (toReset.altBgColor && isAlt) ||
      (toReset.headerBgColor && isHeader)
    ) {
      cell.bgColor = undefined;
    }

    if (
      (toReset.borderColor && !isAlt && !isHeader) ||
      (toReset.altBorderColor && isAlt) ||
      (toReset.headerBorderColor && isHeader)
    ) {
      resetBorderProp("Color");
    }

    if (toReset.borderStyle) {
      resetBorderProp("Style");
    }

    if (toReset.borderWidth) {
      resetBorderProp("Width");
    }

    if (toReset.verticalAlign) {
      cell.verticalAlignment = undefined;
    }
  }
};

const handleFroalaSettings = (props: IProps) => {
  const { settings } = props;
  const {
    tableData,
    tableCols,
    tableRows,
    //
    tableCellTextColor,
    tableCellFontSize,
    tableCellTextAlignment,
    tableCellTextOrientation,
    //
    tableHasHeader,
    tableHeaderTextColor,
    tableHeaderPosition,
    //
    tableHasAlternatingRows,
    tableAlternatingRowsTextColor,
  } = settings;

  const shouldReset = (key: string) => shouldResetSetting(key, props);

  const toReset = {
    fontSize: shouldReset("tableCellFontSize"),
    textAlign: shouldReset("tableCellTextAlignment"),
    textOrientation: shouldReset("tableCellTextOrientation"),
    textColor: shouldReset("tableCellTextColor"),
    altTextColor:
      shouldReset("tableAlternatingRowsTextColor") ||
      shouldReset("tableHasAlternatingRows"),
    headerTextColor: shouldReset("tableHeaderTextColor"),
    //
    hasHeader: shouldReset("tableHasHeader"),
    headerPosition: shouldReset("tableHeaderPosition"),
  };

  let hasAmtOfCellsChanged = false;
  if (
    lastSettings.cellsLength != null &&
    lastSettings.cellsLength !== tableData.length
  ) {
    hasAmtOfCellsChanged = true;
  }
  lastSettings.cellsLength = tableData.length;

  if (!Object.values(toReset).includes(true) && !hasAmtOfCellsChanged) {
    return;
  }

  const fontSize = tableCellFontSize + "px";

  for (let cellIdx = 0; cellIdx < tableData.length; cellIdx++) {
    const cell = tableData[cellIdx];
    const row = Math.floor(cellIdx / tableCols);
    const isCellHeader = isTableCellHeader({
      cellIdx,
      tableCols,
      tableRows,
      tableHasHeader,
      tableHeaderPosition,
    });
    const isAlt = !isCellHeader && tableHasAlternatingRows && row % 2 !== 0;
    const isNewCell = !touchedCells[cell.id];
    touchedCells[cell.id] = true;

    const shouldReplaceColor =
      (toReset.textColor && !isAlt && !isCellHeader) ||
      (toReset.altTextColor && isAlt) ||
      (toReset.headerTextColor && isCellHeader) ||
      toReset.hasHeader ||
      toReset.headerPosition;

    if (shouldReplaceColor) {
      cell.content = stripStyleDecls(cell.content, ["color"]);
    }

    if (toReset.fontSize) {
      cell.hasFroalaFontSize = false;
      cell.content = stripStyleDecls(cell.content, ["font-size"]);
    }

    if (toReset.textAlign) {
      cell.content = stripStyleDecls(cell.content, ["text-align"]);
    }

    if (toReset.textOrientation) {
      cell.content = stripStyleDecls(cell.content, ["text-align", "direction"]);
    }

    const tmpTree = new DOMParser().parseFromString(
      cell.content.length === 0 ? "<p><br></p>" : cell.content,
      "text/html",
    );

    const blocks = tmpTree.body.querySelectorAll(
      "p, li, h1, h2, h3",
    ) as unknown as HTMLElement[];

    blocks.forEach((block) => {
      let span = tmpTree.createElement("span");
      let hasNewSpan = true;

      if (
        block.childNodes.length === 1 &&
        block.childNodes[0].nodeName === "SPAN"
      ) {
        hasNewSpan = false;
        span = block.childNodes[0] as HTMLElement;
      }

      if (shouldReplaceColor || (isNewCell && isCellHeader)) {
        const color = isCellHeader
          ? tableHeaderTextColor
          : isAlt
            ? tableAlternatingRowsTextColor
            : tableCellTextColor;
        block.style.setProperty("color", color);
        span.style.setProperty("color", color);
      }

      if (toReset.fontSize) {
        span.style.setProperty("font-size", fontSize);
      }

      if (toReset.textAlign) {
        block.style.setProperty("text-align", tableCellTextAlignment);
      }

      if (toReset.textOrientation) {
        block.style.setProperty(
          "text-align",
          tableCellTextOrientation === "rtl" ? "right" : "left",
        );
        block.style.setProperty("direction", tableCellTextOrientation);
      }

      if (hasNewSpan) {
        Array.from(block.childNodes).forEach((child) =>
          span.appendChild(child.cloneNode(true)),
        );
        block.innerHTML = "";
        block.appendChild(span);
      }
    });

    cell.content =
      tmpTree.body.firstChild instanceof HTMLElement
        ? tmpTree.body.innerHTML
        : cell.content;
  }
};
